/**
 * [INPUT]: 依赖 ../scorer 的 DiagnoseRequest / DiagnoseResponse 类型；运行期依赖 fetch / AbortController（浏览器+Node 18+ 全局）
 * [OUTPUT]: 对外提供 requestDiagnosis(DiagnoseRequest) → Promise<DiagnoseResponse> 与 DiagnoseError（带 code）
 * [POS]: apps/web/src/api 的后端诊断请求层，唯一与 apps/api 通信的出口；对齐 docs §3.2 / §2 后半段
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../CLAUDE.md
 *
 * 错误码契约（与后端状态码映射）：
 *   INVALID_INPUT (422) / RATE_LIMIT (429) / LLM_UNAVAILABLE (502) /
 *   TIMEOUT (abort) / NETWORK (fetch throw) / UNKNOWN
 */
import type { DiagnoseRequest, DiagnoseResponse } from '../scorer'

/** API 基址。本地开发走 VITE_API_BASE（如 http://localhost:8000）；生产置 Pages 同域或 API 网关。 */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? ''

const DIAGNOSE_URL = `${API_BASE}/v1/reports/diagnose`
const DIAGNOSE_TIMEOUT_MS = 45000

export type DiagnoseErrorCode =
  | 'INVALID_INPUT'
  | 'RATE_LIMIT'
  | 'LLM_UNAVAILABLE'
  | 'TIMEOUT'
  | 'NETWORK'
  | 'UNKNOWN'

export class DiagnoseError extends Error {
  readonly code: DiagnoseErrorCode
  constructor(code: DiagnoseErrorCode, message: string) {
    super(message)
    this.name = 'DiagnoseError'
    this.code = code
  }
}

/** 诊断请求生命周期 UI 状态（App 状态机持有，ResultView 消费）。 */
export type DiagnosisState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'result'; report: DiagnoseResponse }
  | { status: 'error'; code: DiagnoseErrorCode; message: string }

/** 读响应体（容忍非 JSON），用于错误文案组装与诊断。 */
async function readRespBody(resp: Response): Promise<{ detail?: unknown } | null> {
  try {
    return (await resp.json()) as { detail?: unknown }
  } catch {
    return null
  }
}

/** 把 422 的 Pydantic 校验详情转成友好文案，指明越界字段。 */
function describe422(body: { detail?: unknown } | null): string | null {
  const errs = body?.detail
  if (!Array.isArray(errs) || errs.length === 0) return null
  const fields = errs
    .map((e) => {
      const loc = Array.isArray((e as { loc?: unknown[] })?.loc)
        ? (e as { loc: unknown[] }).loc.slice(-1)[0]
        : null
      return typeof loc === 'string' ? loc : null
    })
    .filter((f): f is string => Boolean(f))
  if (fields.length === 0) return null
  return `Metric data out of expected range (${fields.join(', ')} outside physiological bounds). Please try again with a front-facing, eye-level photo in even lighting.`
}

/**
 * 请求 AI 专家诊断。仅发送脱敏 DiagnoseRequest（数值），不含图像。
 * 失败抛 DiagnoseError（带 code），调用方据此展示友好文案与重试策略。
 */
export async function requestDiagnosis(payload: DiagnoseRequest): Promise<DiagnoseResponse> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), DIAGNOSE_TIMEOUT_MS)
  try {
    const resp = await fetch(DIAGNOSE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
    if (resp.ok) {
      return (await resp.json()) as DiagnoseResponse
    }
    const body = await readRespBody(resp)
    switch (resp.status) {
      case 422:
        throw new DiagnoseError('INVALID_INPUT', describe422(body) ?? 'Metric data out of expected range. Please retake the photo and try again.')
      case 429:
        throw new DiagnoseError('RATE_LIMIT', 'Too many requests. Please try again later.')
      case 502:
        throw new DiagnoseError('LLM_UNAVAILABLE', 'AI diagnosis is temporarily unavailable. Please try again later.')
      default:
        throw new DiagnoseError('UNKNOWN', `Diagnosis failed (${resp.status}${body?.detail ? ': ' + body.detail : ''})`)
    }
  } catch (e) {
    if (e instanceof DiagnoseError) throw e
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new DiagnoseError('TIMEOUT', 'Request timed out. Please try again.')
    }
    throw new DiagnoseError('NETWORK', 'Network error. Please check your connection and try again.')
  } finally {
    clearTimeout(timer)
  }
}